import * as THREE from 'three'
import { Vec3 } from 'vec3'
import type { Bot } from 'mineflayer'
import { isEntityAttackable } from 'mineflayer-mouse/dist/attackableEntity'
import type { Block } from 'prismarine-block'

export interface XRWorldRay { origin: Vec3; direction: Vec3 }
interface TargetEntity { id: number; position: Vec3; width: number; height: number }
interface TargetBlock { position: Vec3; intersect: Vec3; face: number }

/** Ray starts at the controller, but reach is still measured from the server player's eye. */
export function raycastControllerTargets<B extends TargetBlock, E extends TargetEntity>(ray: XRWorldRay | null, eye: Vec3, blockRaycast: (origin: Vec3, direction: Vec3, range: number) => B | null, entities: E[], playerId: number, creative: boolean, requestedBlockRange = 5) {
  if (!ray) return { block: null as B | null, entity: null as E | null }
  const handDistance = eye.distanceTo(ray.origin)
  if (handDistance > 1.5 || ![ray.origin.x, ray.origin.y, ray.origin.z, ray.direction.x, ray.direction.y, ray.direction.z].every(Number.isFinite)) return { block: null as B | null, entity: null as E | null }
  if (handDistance > 0.03 && blockRaycast(eye, ray.origin.minus(eye).normalize(), handDistance - 0.02)) return { block: null as B | null, entity: null as E | null }
  const direction = ray.direction.normalize()
  const blockReach = Math.min(5, requestedBlockRange)
  const blockHit = blockRaycast(ray.origin, direction, blockReach + handDistance)
  const block = blockHit && eye.distanceTo(blockHit.intersect) <= blockReach ? blockHit : null
  let nearest = blockHit ? ray.origin.distanceTo(blockHit.intersect) : Infinity
  let entity: E | null = null
  const threeRay = new THREE.Ray(new THREE.Vector3().copy(ray.origin), new THREE.Vector3().copy(direction))
  const hit = new THREE.Vector3()
  const entityReach = creative ? 5 : 3
  for (const candidate of entities) {
    if (candidate.id === playerId || !candidate.position || candidate.width <= 0 || candidate.height <= 0) continue
    const p = candidate.position; const halfWidth = candidate.width / 2
    const box = new THREE.Box3(new THREE.Vector3(p.x - halfWidth, p.y, p.z - halfWidth), new THREE.Vector3(p.x + halfWidth, p.y + candidate.height, p.z + halfWidth))
    if (!threeRay.intersectBox(box, hit)) continue
    const distance = threeRay.origin.distanceTo(hit)
    if (distance < nearest && Math.hypot(hit.x - eye.x, hit.y - eye.y, hit.z - eye.z) <= entityReach) { nearest = distance; entity = candidate }
  }
  return { block, entity }
}

/** Override only the public cursor query; keep mineflayer-mouse's attack, use, dig and place handlers. */
export function installXRInteractionRay(bot: Bot, getRay: () => XRWorldRay | null, active: () => boolean) {
  const originalBlock = bot.blockAtCursor
  const originalCursor = bot.mouse.getCursorState
  const query = (range = 5, includeEntities = true) => {
    if (!bot.entity) return { block: null, entity: null }
    const eye = bot.entity.position.offset(0, bot.controlState.sneak ? 1.27 : 1.62, 0)
    const entities = includeEntities ? Object.values(bot.entities).filter(entity => entity !== bot.entity && entity.name && isEntityAttackable(bot.registry, entity)) : []
    return raycastControllerTargets(getRay(), eye, (origin, direction, distance) => bot.world.raycast(origin, direction, distance) as (Block & TargetBlock) | null, entities, bot.entity.id, bot.game.gameMode === 'creative', range)
  }
  bot.blockAtCursor = function (range, ...args) {
    if (!active()) return originalBlock.call(bot, range, ...args)
    return query(range, false).block
  }
  bot.mouse.getCursorState = function () {
    if (!active()) return originalCursor.call(this)
    const { block, entity } = query()
    const cursorBlock = entity ? null : block
    if (entity && this.breakStartTime !== undefined) this.stopDiggingCompletely('entity interference')
    const cursorBlockDiggable = cursorBlock && bot.game.gameMode !== 'spectator' && (bot.game.gameMode === 'creative' || (bot.game.gameMode !== 'adventure' && bot.canDigBlock(cursorBlock))) ? cursorBlock : null
    const previous = this.cursorBlock
    const cursorPositionChanged = !!cursorBlock !== !!previous || !!(cursorBlock && previous && !cursorBlock.position.equals(previous.position))
    const cursorChanged = cursorPositionChanged || !!(cursorBlock && previous && cursorBlock.stateId !== previous.stateId)
    if (cursorChanged) bot.emit('highlightCursorBlock', cursorBlock ? { block: cursorBlock } : undefined)
    this.cursorBlock = cursorBlock
    return { cursorBlock, cursorBlockDiggable, cursorChanged, cursorPositionChanged, entity }
  }
  return () => { bot.blockAtCursor = originalBlock; bot.mouse.getCursorState = originalCursor }
}
